import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Shared rules for the student "Joined Month" field (month + year only).
 *
 * Storage/display are deliberately separate:
 *   • the API is sent a normalized "YYYY-MM" string, and
 *   • the picker shows "July 2026".
 *
 * The column is a free-form string that historically held several shapes
 * ("2026-07", "07/2026", "July 2026", "2026-07-01"), so every read goes through
 * parseJoinedMonth() which tolerates all of them.
 */

// Business floor for the year range — configurable, NOT an open 1900–2100 span.
export const JOINED_MONTH_MIN_DATE = dayjs("2010-01-01").startOf("month");

/** Upper bound = the current month (evaluated at call time, never a stale value). */
export const joinedMonthMaxDate = (): Dayjs => dayjs();

// Known stored shapes, tried strictly before any lenient fallback.
const ACCEPTED_FORMATS = [
  "YYYY-MM",
  "YYYY-MM-DD",
  "YYYY/MM",
  "MM/YYYY",
  "M/YYYY",
  "MMMM YYYY",
  "MMM YYYY",
];

/**
 * Normalize any stored joined-month value into a Dayjs at the first of the
 * month, or null if it can't be understood. Accepts an existing Dayjs too.
 */
export const parseJoinedMonth = (value?: string | Dayjs | null): Dayjs | null => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value.startOf("month") : null;

  const raw = String(value).trim();
  if (!raw) return null;

  const strict = dayjs(raw, ACCEPTED_FORMATS, true);
  if (strict.isValid()) return strict.startOf("month");

  // Lenient fallback for anything else parseable (e.g. full ISO timestamps).
  const loose = dayjs(raw);
  return loose.isValid() ? loose.startOf("month") : null;
};

/** The value SENT to the backend: "YYYY-MM", or null when empty/invalid. */
export const formatJoinedMonthForApi = (value?: Dayjs | null): string | null => {
  if (!value || !dayjs.isDayjs(value) || !value.isValid()) return null;
  return value.format("YYYY-MM");
};

/** Human label: "July 2026" (used by read-only displays). */
export const formatJoinedMonthLabel = (value?: string | Dayjs | null): string => {
  const parsed = parseJoinedMonth(value);
  return parsed ? parsed.format("MMMM YYYY") : "—";
};

/** True when the month is later than the current month. */
export const isJoinedMonthInFuture = (value?: Dayjs | null): boolean => {
  if (!value || !dayjs.isDayjs(value) || !value.isValid()) return false;
  return value.startOf("month").isAfter(dayjs().startOf("month"));
};
